/**
 * aethereum ticket — lightweight work tickets for the team.
 *
 * Tickets are the durable task rail mission control writes into: create one,
 * list them, assign to a handle, advance status, or close. They ride the same
 * hosted command bridge + peek context rail as directives and prompts.
 */

import { resolveHostedConfig, type HostedFlags } from "./hosted-config.js";
import { HostedWriteClient, type Ticket } from "../hive/hosted-client.js";

export interface TicketOptions extends HostedFlags {
  env?: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string };
  fetchImpl?: typeof fetch;
}

const PRIORITIES = ["low", "med", "high"] as const;
const STATUSES = ["open", "doing", "done", "closed"] as const;
const RECURS = ["daily", "weekly"] as const;

/** YYYY-MM-DD -> epoch ms (UTC midnight). Returns undefined for unparseable input. */
function parseDue(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw.trim());
  if (!m) return undefined;
  const ms = Date.parse(`${raw.trim()}T00:00:00Z`);
  return Number.isNaN(ms) ? undefined : ms;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

function renderTickets(tickets: Ticket[]): string {
  if (tickets.length === 0) return "No tickets yet.";
  return tickets
    .map((t) => {
      const head = `#${shortId(t.ticketId)} [${t.status}] ${t.title}`;
      const who = t.assignee ? ` — ${t.assignee}` : "";
      const prio = t.priority ? ` (${t.priority})` : "";
      const recur = t.recur ? ` ↻${t.recur}` : "";
      return `${head}${who}${prio}${recur}`;
    })
    .join("\n");
}

export async function runTicket(args: string[], options: TicketOptions = {}): Promise<void> {
  const sub = args[0];
  const rest = args.slice(1);

  const cfg = resolveHostedConfig(options, options.env ?? process.env);
  if ("error" in cfg) {
    console.error(cfg.error);
    process.exit(1);
    return;
  }

  const client = new HostedWriteClient(cfg.baseUrl, cfg.token, options.fetchImpl ?? fetch);

  // local flag readers (operate over `rest` so positional args stay clean)
  const flag = (name: string): string | undefined => {
    const idx = rest.indexOf(`--${name}`);
    if (idx === -1) return undefined;
    const v = rest[idx + 1];
    if (v === undefined || v.startsWith("--")) return undefined;
    return v;
  };
  // positional args = everything before the first flag
  const firstFlag = rest.findIndex((a) => a.startsWith("--"));
  const positionals = firstFlag === -1 ? rest : rest.slice(0, firstFlag);

  try {
    switch (sub) {
      case "create": {
        const title = positionals.join(" ").trim();
        if (!title) {
          console.error('Usage: aethereum ticket create "<title>" [--assignee <h>] [--priority low|med|high] [--due YYYY-MM-DD] [--recur daily|weekly]');
          process.exit(1);
          return;
        }
        const priorityRaw = flag("priority");
        const priority = PRIORITIES.includes(priorityRaw as (typeof PRIORITIES)[number])
          ? (priorityRaw as (typeof PRIORITIES)[number])
          : undefined;
        const recurRaw = flag("recur");
        const recur = RECURS.includes(recurRaw as (typeof RECURS)[number])
          ? (recurRaw as (typeof RECURS)[number])
          : undefined;
        const { ticketId } = await client.createTicket({
          title,
          assignee: flag("assignee"),
          priority,
          due: parseDue(flag("due")),
          recur,
        });
        console.log(`✓ ticket created #${shortId(ticketId)} — ${title}${recur ? ` (↻${recur})` : ""}`);
        return;
      }

      case "list": {
        const tickets = await client.fetchTickets();
        console.log(renderTickets(tickets));
        return;
      }

      case "assign": {
        const [id, handle] = positionals;
        if (!id || !handle) {
          console.error("Usage: aethereum ticket assign <id> <handle>");
          process.exit(1);
          return;
        }
        await client.updateTicket({ ticketId: id, assignee: handle });
        console.log(`✓ #${shortId(id)} assigned to ${handle}`);
        return;
      }

      case "close": {
        const [id] = positionals;
        if (!id) {
          console.error("Usage: aethereum ticket close <id>");
          process.exit(1);
          return;
        }
        await client.updateTicket({ ticketId: id, status: "closed" });
        console.log(`✓ #${shortId(id)} closed`);
        return;
      }

      case "move": {
        const [id, status] = positionals;
        if (!id || !STATUSES.includes(status as (typeof STATUSES)[number])) {
          console.error("Usage: aethereum ticket move <id> open|doing|done|closed");
          process.exit(1);
          return;
        }
        await client.updateTicket({ ticketId: id, status: status as (typeof STATUSES)[number] });
        console.log(`✓ #${shortId(id)} → ${status}`);
        return;
      }

      case "doing":
      case "done": {
        const [id] = positionals;
        if (!id) {
          console.error(`Usage: aethereum ticket ${sub} <id>`);
          process.exit(1);
          return;
        }
        await client.updateTicket({ ticketId: id, status: sub });
        console.log(`✓ #${shortId(id)} → ${sub}`);
        return;
      }

      default:
        console.error(
          "Usage: aethereum ticket create|list|assign|close|move|doing|done [...]",
        );
        process.exit(1);
    }
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }
}
